import { defineStore } from 'pinia'
import { normalizeBoolean } from '~/utils/normalizeBoolean'

export interface SubscriptionData {
  hasSubscription: boolean
  subscription?: {
    id: string
    status: string
    planId: string
    customerId: string
    currentPeriodEnd: string
  }
  customer?: { id: string; email: string }
}

export interface UserSubscriptionState {
  subscriptionData: SubscriptionData | null
  isLoading: boolean
  lastChecked: Date | null
  error: string | null
  isValid: boolean
}

export const useSubscriptionStore = defineStore('subscription', {
  state: (): UserSubscriptionState => ({
    subscriptionData: null, isLoading: false, lastChecked: null, error: null, isValid: false
  }),

  getters: {
    hasActiveSubscription: (state): boolean => state.subscriptionData?.hasSubscription || false,
    subscriptionStatus: (state): string | null => state.subscriptionData?.subscription?.status || null,
    planId: (state): string | null => state.subscriptionData?.subscription?.planId || null,

    isDataStale: (state): boolean => {
      if (!state.lastChecked) return true
      return state.lastChecked < new Date(Date.now() - 5 * 60 * 1000)
    },

    hasValidSubscription: (state): boolean => {
      const user = useSupabaseUser()
      if (!user.value) return false

      const userData = user.value.user_metadata
      const hasActiveStripeSubscription = state.subscriptionData?.hasSubscription
      const hasCompletedOnboarding = normalizeBoolean(userData?.onboarding_completed)
      const hasSelectedPlan = normalizeBoolean(userData?.plan_selected)
      const selectedPlan = userData?.selected_plan
      const joinedViaInvitation = normalizeBoolean(userData?.joined_via_invitation)

      if (state.subscriptionData !== null && !hasActiveStripeSubscription) {
        if (hasCompletedOnboarding && hasSelectedPlan && selectedPlan === 'free') return true
        if (joinedViaInvitation && hasCompletedOnboarding) return true
        return false
      }
      if (hasActiveStripeSubscription) return true
      if (hasCompletedOnboarding && hasSelectedPlan) return true
      return false
    }
  },

  actions: {
    async checkSubscription(force: boolean = false): Promise<SubscriptionData | null> {
      if (!force && !this.isDataStale && this.subscriptionData && !this.error) return this.subscriptionData

      this.isLoading = true
      this.error = null
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Subscription check timeout')), 8000)
        })
        const fetchPromise = $fetch<SubscriptionData>('/api/stripe/user-subscription')
        const response = await Promise.race([fetchPromise, timeoutPromise])

        if (response && typeof response === 'object') {
          this.subscriptionData = response
          this.lastChecked = new Date()
          this.isValid = this.hasValidSubscription
        }
        return this.subscriptionData
      } catch (error: any) {
        console.error('Failed to check subscription:', error)
        this.error = error.message || 'Failed to check subscription'
        if (!this.subscriptionData || (this.lastChecked && this.lastChecked < new Date(Date.now() - 30 * 60 * 1000))) {
          this.subscriptionData = null
        }
        return this.subscriptionData
      } finally {
        this.isLoading = false
      }
    },

    async validateSubscriptionStatus(): Promise<boolean> {
      await this.checkSubscription()
      return this.hasValidSubscription
    },

    clearSubscriptionData() {
      this.subscriptionData = null; this.lastChecked = null; this.error = null; this.isValid = false
    },

    updateSubscriptionData(data: SubscriptionData) {
      this.subscriptionData = data; this.lastChecked = new Date()
      this.isValid = this.hasValidSubscription; this.error = null
    }
  }
})
